/* eslint-disable react/jsx-max-props-per-line */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import validate from 'validate.js';
import { Stack, Typography } from '@mui/material';
import { DefaultSelectV2, DialogDefaultV2 } from '../../../components/v2';
import { isItemOutro } from './statusLabels';
import PedidoVinculadoForm, { emptyPedidoVinculado } from './PedidoVinculadoForm';

const COPY = {
  APROVADO: {
    title: 'Confirmar aprovação',
    paragraphs: [
      'Este item será atendido com o estoque e fica Aprovado.',
      'Ele só sai daqui quando você criar uma entrega na aba Entregas — sozinho ou agrupado com outros aprovados.',
      'Confirmar grava a decisão e a origem no estoque. A entrega continua sendo criada na aba Entregas.',
    ],
  },
  REJEITADO: {
    title: 'Confirmar rejeição',
    paragraphs: [
      'Este item não será atendido. O solicitante vê o motivo no app.',
      'Não gera entrega nem pedido de compra.',
      'Confirmar grava agora.',
    ],
  },
  PARA_PEDIDO: {
    title: 'Pedido vinculado',
    paragraphs: [
      'Este item não usa o estoque atual. Confirmar cria o pedido de almoxarifado já ligado a este item.',
      'Não há status de pedido. Depois de gravar, o item já pode entrar numa entrega.',
    ],
  },
};

const pedidoSchema = {
  produto: { presence: { allowEmpty: false, message: 'é obrigatório' } },
  local: { presence: { allowEmpty: false, message: 'é obrigatório' } },
  quantidade: { presence: { allowEmpty: false, message: 'é obrigatório' } },
  valorUnitario: { presence: { allowEmpty: false, message: 'é obrigatório' } },
  fornecedor: { presence: { allowEmpty: false, message: 'é obrigatório' } },
};

export const itemNeedsPedidoVinculado = (item) =>
  !!item && item.tipoItem === 'PRODUTO' && !isItemOutro(item);

const DecisaoContextoDialog = ({
  open,
  item,
  itemNome,
  decisao,
  isRedecisao,
  onClose,
  onConfirm,
  confirmLoading,
  requiresLocal,
  localOptions,
}) => {
  const copy = COPY[decisao] || COPY.APROVADO;
  const needsPedido = decisao === 'PARA_PEDIDO' && itemNeedsPedidoVinculado(item);
  const [local, setLocal] = useState('');
  const [localError, setLocalError] = useState(false);
  const [pedido, setPedido] = useState(() => emptyPedidoVinculado(item));
  const [pedidoErrors, setPedidoErrors] = useState({});

  useEffect(() => {
    if (!open) {
      setLocal('');
      setLocalError(false);
      setPedido(emptyPedidoVinculado(item));
      setPedidoErrors({});
      return;
    }
    setPedido(emptyPedidoVinculado(item));
    setPedidoErrors({});
    if (requiresLocal && localOptions.length === 1) {
      setLocal(localOptions[0].id);
    } else {
      setLocal('');
    }
    setLocalError(false);
  }, [open, requiresLocal, localOptions, item]);

  const noLocalAvailable = requiresLocal && !localOptions.length;
  const handleConfirm = () => {
    if (needsPedido) {
      const nextErrors = validate(pedido, pedidoSchema) || {};
      setPedidoErrors(nextErrors);
      if (Object.keys(nextErrors).length) return;
      onConfirm({
        pedido: {
          local: pedido.local,
          tipoEntrada: 'FORNECEDOR',
          fornecedor: pedido.fornecedor.trim(),
          produtoId: pedido.produto,
          quantidade: Number(pedido.quantidade),
          valorUnitario: Number(pedido.valorUnitario),
          valorTotal: pedido.valorTotal != null
            ? Number(pedido.valorTotal)
            : Number(pedido.quantidade) * Number(pedido.valorUnitario),
          devolucaoUso: false,
          tamanho: item?.tamanho || null,
        },
      });
      return;
    }
    if (requiresLocal) {
      if (!local) {
        setLocalError(true);
        return;
      }
      onConfirm({ local });
      return;
    }
    onConfirm({});
  };

  return (
    <DialogDefaultV2
      open={open}
      handlerCancel={onClose}
      handlerConfirm={handleConfirm}
      title={copy.title}
      confirmMessage={needsPedido ? 'Criar pedido e gravar' : 'Confirmar'}
      cancelMessage="Voltar"
      confirmLoading={confirmLoading}
      confirmLoadingMessage="Gravando…"
      disabledConfirmButton={noLocalAvailable}
      maxWidth={needsPedido ? 'md' : 'sm'}
      fullWidth
      useFullScreen={false}
    >
      <Stack spacing={1.5}>
        {itemNome ? (
          <Typography variant="body2" fontWeight={600}>
            {itemNome}
          </Typography>
        ) : null}
        {copy.paragraphs.map((text) => (
          <Typography key={text} variant="body2">
            {text}
          </Typography>
        ))}
        {isRedecisao ? (
          <Typography variant="body2">
            Isso substitui a decisão já gravada. Pedido de saída de estoque e pedido
            ligados a este item são estornados ou cancelados, se ainda for possível.
          </Typography>
        ) : null}
        {needsPedido ? (
          <PedidoVinculadoForm
            values={pedido}
            errors={pedidoErrors}
            onChange={setPedido}
          />
        ) : null}
        {requiresLocal ? (
          noLocalAvailable ? (
            <Typography variant="body2" color="warning.main">
              Não há saldo em nenhum local. Use &quot;Para pedido&quot; para compra externa.
            </Typography>
          ) : (
            <DefaultSelectV2
              label="Local de retirada"
              name="local"
              isRequired
              options={localOptions}
              value={local}
              error={localError && !local}
              helperText="Selecione o local de retirada"
              onSelect={(event) => {
                setLocal(event.target.value);
                setLocalError(false);
              }}
            />
          )
        ) : null}
      </Stack>
    </DialogDefaultV2>
  );
};

DecisaoContextoDialog.propTypes = {
  open: PropTypes.bool,
  item: PropTypes.object,
  itemNome: PropTypes.string,
  decisao: PropTypes.string,
  isRedecisao: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  confirmLoading: PropTypes.bool,
  requiresLocal: PropTypes.bool,
  localOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    nome: PropTypes.string.isRequired,
  })),
};

DecisaoContextoDialog.defaultProps = {
  open: false,
  item: null,
  itemNome: '',
  decisao: 'APROVADO',
  isRedecisao: false,
  confirmLoading: false,
  requiresLocal: false,
  localOptions: [],
};

export default DecisaoContextoDialog;
